import React from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const SelectTradesModal = ({
  open,
  onClose,
  onBack,
  onNext,
  selectedTrades,
  searchQuery,
  onSearchChange,
  onTradeSelection,
  onSelectAllTrades,
  mockTradeData,
  strategiesData = []
}) => {
  // Function to find account ID for a given strategy ID
  const getAccountIdForStrategy = (strategyId) => {
  
    // Look for user strategy where strategy.id matches the order's strategy ID
    const userStrategy = strategiesData.find(s => s.strategy?.id === strategyId);
  
    const accountId = userStrategy?.accountId || null;
   
    return accountId;
  };

  // Transform API data to CSV format for buy/sell orders (same as PreviewModal)
  const transformToCSVFormat = (orders) => {
    const csvRecords = [];
    
    if (!Array.isArray(orders) || orders.length === 0) {
      return csvRecords;
    }

    orders.forEach((order, orderIndex) => {
     
      
      // Skip orders with type MANAGER_DISCRETION
      if (order.type === 'MANAGER_DISCRETION') {
       
        return;
      }
      
      // Use full names instead of abbreviated codes
      const actionMap = {
        'BUY': 'BUY',
        'SELL': 'SELL',
        'EXCHANGE': 'EXCHANGE',
        'SWAP': 'SWAP'
      };

      // Use full security type names
      const securityTypeMap = {
        'STK': 'STOCK', // Equity
        'MUTUAL_FUND': 'MUTUAL_FUND', // Mutual Fund
        'ETF': 'ETF' // ETF
      };

      // Use full order type names
      const orderTypeMap = {
        'MKT': 'MARKET', // Market Order
        'LMT': 'LIMIT', // Limit Order
        'STP': 'STOP', // Stop Order
        'STL': 'STOP_LIMIT', // Stop Limit Order
        'TSP': 'TRAILING_STOP_PERCENT', // Trailing Stop Percent
        'TSD': 'TRAILING_STOP_DOLLAR' // Trailing Stop Dollar
      };

      // Get security type from strategy
      const securityType = securityTypeMap[order.strategy?.securityType] || order.strategy?.securityType || 'STOCK';
      
      // Get order type
      const orderType = orderTypeMap[order.type] || order.type || 'MARKET';
      
      // Check if we should show limit price column
      const showLimitPrice = order.type === 'LMT';
      
      // Time in force
      const timeInForce = order.timeInForce || 'DAY';
      
      // Determine reinvest settings based on security type
      const isMutualFund = order.strategy?.securityType === 'MUTUAL_FUND';
      const reinvestDividends = isMutualFund ? 'Y' : '';
      const reinvestCapitalGains = isMutualFund ? 'Y' : '';
      const transactionFee = isMutualFund ? 'Y' : '';

      if (order.transactions && order.transactions.length > 0) {
        // Create a record for each transaction
        order.transactions.forEach((transaction, transactionIndex) => {
         
          
          const record = {
            'Account Number': getAccountIdForStrategy(order.strategy?.id) || null, // Get account ID from strategies data
            'Account Type': '', // Leave blank as per requirements
            'Security Type': securityType,
            'Action': actionMap[order.action] || order.action || 'BUY',
            'Quantity Type': null, // Set to null as requested
            'Quantity': transaction.quantity || '',
            'Symbol': transaction.asset?.symbol || 'N/A',
            'Exchange/Swap to Symbol': '', // Leave blank for now
            'Order Type': orderType,
            'Time in Force': timeInForce,
            'Do Not Reduce': '', // Leave blank as per requirements
            'All or None': '', // Leave blank as per requirements
            'Reinvest Dividends': reinvestDividends,
            'Transaction Fee': transactionFee,
            'Reinvest Capital Gains': reinvestCapitalGains
          };

          // Add limit price only if order type is LMT
          if (showLimitPrice) {
            record['Limit Price'] = transaction.limitPrice || '';
          } else {
            record['Stop Price'] = ''; // Leave blank for non-limit orders
          }

          csvRecords.push(record);
        });
      } else {
        // If no transactions, create a single record with order data
        const record = {
          'Account Number': getAccountIdForStrategy(order.strategy?.id) || null, // Get account ID from strategies data
          'Account Type': '',
          'Security Type': securityType,
          'Action': actionMap[order.action] || order.action || 'BUY',
          'Quantity Type': null,
          'Quantity': order.amount || '',
          'Symbol': 'N/A',
          'Exchange/Swap to Symbol': '',
          'Order Type': orderType,
          'Time in Force': timeInForce,
          'Do Not Reduce': '',
          'All or None': '',
          'Reinvest Dividends': reinvestDividends,
          'Transaction Fee': transactionFee,
          'Reinvest Capital Gains': reinvestCapitalGains
        };

        // Add limit price only if order type is LMT
        if (showLimitPrice) {
          record['Limit Price'] = order.limitPrice || '';
        } else {
          record['Stop Price'] = ''; // Leave blank for non-limit orders
        }

        csvRecords.push(record);
      }
    });

    return csvRecords;
  };

  
  
  // Transform the mockTradeData (which contains raw order data) to CSV format
  const csvData = transformToCSVFormat(mockTradeData);
  
  // Filter csvData based on search query and maintain original indices
  const filteredCsvData = csvData.map((row, originalIndex) => ({ ...row, _originalIndex: originalIndex }))
    .filter(row => {
      if (!searchQuery || searchQuery.trim() === '') {
        return true; // Show all data if no search query
      }
      
      const searchTerm = searchQuery.toLowerCase().trim();
      
      // Search across all visible fields in the CSV data (excluding the _originalIndex)
      return Object.entries(row).some(([key, value]) => {
        if (key === '_originalIndex' || value === null || value === undefined) {
          return false;
        }
        
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(searchTerm);
      });
    });
  
  // Check if we have any limit orders to determine if we should show the Limit Price column
  const hasLimitOrders = filteredCsvData.some(record => record['Order Type'] === 'LIMIT');
  
  // Check if we need to show scroll indicators
  const needsVerticalScroll = filteredCsvData.length > 5; // Show vertical scroll indicator if more than 5 records
  const needsHorizontalScroll = true; // Always show horizontal scroll indicator due to wide columns

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="select-trades-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <Box sx={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '1400px', // Increased width for CSV format
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 0 24px'
        }}>
          <Typography variant="h4" sx={{
            color: '#141218',
            fontFamily: 'Founders Grotesk, sans-serif',
            fontSize: '24px',
            fontWeight: '600',
            margin: '0'
          }}>
            Select Trades to Download (CSV Format)
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <CloseIcon sx={{ fontSize: 24, color: '#666' }} />
          </IconButton>
        </Box>

        {/* Search and Filter Section */}
        <Box sx={{ padding: '24px 24px 16px 24px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
          }}>
            {/* Search Box */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              border: '1px solid #E6E6E6',
              borderRadius: '6px',
              backgroundColor: 'white',
              flex: '1',
              maxWidth: '400px'
            }}>
              <Box sx={{
                fontSize: '16px',
                color: '#666'
              }}>
                🔍
              </Box>
              <input
                type="text"
                placeholder="Search trades..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                style={{
                  border: 'none',
                  outline: 'none',
                  flex: 1,
                  fontSize: '14px',
                  fontFamily: 'Roboto, sans-serif'
                }}
              />
            </Box>

            {/* Filter By Strategy
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              border: '1px solid #E6E6E6',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}>
              <Typography sx={{
                color: '#141218',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: '400'
              }}>
                Filter By Strategy
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#666' }} />
            </Box> */}

            {/* Filter By Status */}
            {/* <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              border: '1px solid #E6E6E6',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}>
              <Typography sx={{
                color: '#141218',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: '400'
              }}>
                Filter By Status
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#666' }} />
            </Box> */}

            {/* Sort */}
            {/* <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px',
              border: '1px solid #E6E6E6',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}>
              <Typography sx={{
                color: '#141218',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: '400'
              }}>
                Sort
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#666' }} />
            </Box> */}
          </Box>
        </Box>

        {/* Record Count */}
        <Box sx={{ padding: '0 24px 8px 24px' }}>
          <Typography sx={{
            color: '#666',
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: '400'
          }}>
            Showing {filteredCsvData.length} records • {selectedTrades.length} selected
            {needsVerticalScroll && (
              <span style={{ color: '#1344EB', fontWeight: '500' }}> • Scroll to view all</span>
            )}
          </Typography>
        </Box>

        {/* CSV Format Table */}
        <Box sx={{ padding: '0 24px 24px 24px' }}>
          <Box sx={{
            border: '1px solid #E6E6E6',
            borderRadius: '8px',
            overflow: 'hidden',
            maxHeight: '350px', // Reduced height to ensure scroll appears with 5+ records
            overflowY: 'auto',
            overflowX: 'auto',
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '4px',
              '&:hover': {
                background: '#a8a8a8'
              }
            },
            // Ensure smooth scrolling
            scrollBehavior: 'smooth'
          }}>
            {/* Table Header */}
            <Box sx={{
              display: 'flex',
              backgroundColor: '#F8F9FA',
              borderBottom: '1px solid #E6E6E6',
              position: 'sticky',
              top: 0,
              zIndex: 2,
              minWidth: '2500px', // Increased width for CSV format columns with full names
              width: '100%',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)' // Add shadow to make sticky header more visible
            }}>
              <Box sx={{ 
                flex: '0 0 50px', 
                padding: '12px 16px', 
                display: 'flex', 
                alignItems: 'center',
                backgroundColor: '#F8F9FA'
              }}>
                <input
                  type="checkbox"
                  checked={filteredCsvData.length > 0 && filteredCsvData.every(row => selectedTrades.includes(row._originalIndex))}
                  onChange={onSelectAllTrades}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer'
                  }}
                />
              </Box>
              {Object.keys(filteredCsvData[0] || {}).filter(header => hasLimitOrders || header !== 'Limit Price').map((header, index) => (
                <Box key={index} sx={{
                  flex: header === 'Account Number' || header === 'Symbol' ? '1.2' : 
                        header === 'Exchange/Swap to Symbol' ? '1.5' : '1',
                  padding: '12px 16px',
                  borderRight: index < Object.keys(filteredCsvData[0] || {}).filter(h => hasLimitOrders || h !== 'Limit Price').length - 1 ? '1px solid #E6E6E6' : 'none',
                  minWidth: header === 'Account Number' ? '150px' : 
                            header === 'Account Type' ? '120px' :
                            header === 'Security Type' ? '180px' : // Increased for full names
                            header === 'Action' ? '120px' : // Increased for full names
                            header === 'Quantity Type' ? '120px' :
                            header === 'Quantity' ? '120px' :
                            header === 'Symbol' ? '150px' :
                            header === 'Exchange/Swap to Symbol' ? '180px' :
                            header === 'Order Type' ? '200px' : // Increased for full names
                            header === 'Limit Price' ? '120px' :
                            header === 'Stop Price' ? '120px' :
                            header === 'Time in Force' ? '150px' :
                            header === 'Do Not Reduce' ? '150px' :
                            header === 'All or None' ? '120px' :
                            header === 'Reinvest Dividends' ? '180px' :
                            header === 'Transaction Fee' ? '150px' :
                            header === 'Reinvest Capital Gains' ? '200px' : '120px',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#F8F9FA'
                }}>
                  <Typography sx={{
                    color: '#141218',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {header}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Table Rows */}
            {filteredCsvData && filteredCsvData.length > 0 ? (
              filteredCsvData.map((row, index) => (
                <Box key={index} sx={{
                  display: 'flex',
                  borderBottom: index < filteredCsvData.length - 1 ? '1px solid #E6E6E6' : 'none',
                  minWidth: '2500px', // Increased width for CSV format columns with full names
                  width: '100%',
                  '&:hover': {
                    backgroundColor: '#F8F9FA'
                  }
                }}>
                  <Box sx={{ 
                    flex: '0 0 50px', 
                    padding: '8px 16px', 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedTrades.includes(row._originalIndex)}
                      onChange={() => onTradeSelection(row._originalIndex)}
                      style={{
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer'
                      }}
                    />
                  </Box>
                  {Object.keys(row).filter(header => hasLimitOrders || header !== 'Limit Price').map((header, colIndex) => (
                    <Box key={colIndex} sx={{
                      flex: header === 'Account Number' || header === 'Symbol' ? '1.2' : 
                            header === 'Exchange/Swap to Symbol' ? '1.5' : '1',
                      padding: '8px 16px',
                      borderRight: colIndex < Object.keys(row).filter(h => hasLimitOrders || h !== 'Limit Price').length - 1 ? '1px solid #E6E6E6' : 'none',
                      minWidth: header === 'Account Number' ? '150px' : 
                                header === 'Account Type' ? '120px' :
                                header === 'Security Type' ? '180px' : // Increased for full names
                                header === 'Action' ? '120px' : // Increased for full names
                                header === 'Quantity Type' ? '120px' :
                                header === 'Quantity' ? '120px' :
                                header === 'Symbol' ? '150px' :
                                header === 'Exchange/Swap to Symbol' ? '180px' :
                                header === 'Order Type' ? '200px' : // Increased for full names
                                header === 'Limit Price' ? '120px' :
                                header === 'Stop Price' ? '120px' :
                                header === 'Time in Force' ? '150px' :
                                header === 'Do Not Reduce' ? '150px' :
                                header === 'All or None' ? '120px' :
                                header === 'Reinvest Dividends' ? '180px' :
                                header === 'Transaction Fee' ? '150px' :
                                header === 'Reinvest Capital Gains' ? '200px' : '120px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}>
                      <Typography sx={{
                        color: '#141218',
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        fontWeight: '400',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {row[header] || 'N/A'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))
            ) : (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '40px 20px',
                minWidth: '2500px',
                width: '100%'
              }}>
                <Typography sx={{
                  color: '#666',
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: '14px',
                  fontWeight: '400'
                }}>
                  No trade data available
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Scroll indicators */}
          {(needsVerticalScroll || needsHorizontalScroll) && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '8px',
              color: '#666',
              fontSize: '12px',
              fontStyle: 'italic'
            }}>
              {needsHorizontalScroll && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>← Scroll horizontally to view all columns →</span>
                </Box>
              )}
              {needsVerticalScroll && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <span>↑ Scroll vertically to view all records ↑</span>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderTop: '1px solid #E6E6E6',
          backgroundColor: '#FAFAFA'
        }}>
          <Button
            onClick={onBack}
            startIcon={<KeyboardArrowUpIcon />}
            sx={{
              color: '#666',
              textTransform: 'none',
              fontWeight: '500',
              '&:hover': {
                backgroundColor: '#F5F5F5'
              }
            }}
          >
            Back
          </Button>
          
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <Typography sx={{
              color: '#666',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: '400'
            }}>
              {selectedTrades.length} of {filteredCsvData.length} trades selected
            </Typography>
            
            <Button
              onClick={onNext}
              variant="contained"
              startIcon={<KeyboardArrowDownIcon />}
              disabled={selectedTrades.length === 0}
              sx={{
                backgroundColor: selectedTrades.length === 0 ? '#E0E0E0' : '#1344EB',
                color: 'white',
                textTransform: 'none',
                fontWeight: '500',
                padding: '8px 24px',
                '&:hover': {
                  backgroundColor: selectedTrades.length === 0 ? '#E0E0E0' : '#0f3ad1'
                }
              }}
            >
              Next ({selectedTrades.length} selected)
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SelectTradesModal;